import React, {useState, useEffect} from 'react';
import {Transcription} from '../domain/types';
import {DeleteConfirmationModal} from '../components/DeleteConfirmationModal';
import {AudioPathModal} from '../components/AudioPathModal';
import {useNavigate, useParams} from 'react-router-dom';
import {
  useAppDispatch,
  useAppSelector
} from '../store/hooks';
import {
  fetchTranscriptions,
  deleteTranscription,
  startTranscription,
  pollTranscriptionStatus,
} from '../store/slices/transcriptionsThunks';
import {
  invalidateCache,
  addTranscription,
  setSelectedTranscription
} from '../store/slices/transcriptionsSlice';
import {uploadCsvToWorker} from '../store/slices/conversionsThunks';
import {useToast} from '../components/Toast';
import {pickFile} from '../lib/browserFiles';
import {useOwnedProjectId} from '../components/useOwnedProjectId';
import {defaultProjectIdSelector} from '../store/selectors/projects.selectors';

interface TranscriptionsPageProps {
    projectId?: string;
    onNewTranscription: () => void;
}

const TranscriptionsPage: React.FC<TranscriptionsPageProps> = ({
  onNewTranscription
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {projectId: routeProjectId} = useParams<{projectId: string}>();
  const {transcriptions, loading: isLoading, error: reduxError} = useAppSelector(state => state.transcriptions);
  const {showToast} = useToast();

  // A route project id belonging to another account is a 403, so it is checked against this
  // account's projects before any fetch uses it. See `useOwnedProjectId`.
  const {projectId, project, isResolving: isResolvingProject} = useOwnedProjectId(routeProjectId);
  const defaultProjectId = useAppSelector(defaultProjectIdSelector);

  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [transcriptionToDelete, setTranscriptionToDelete] = useState<Transcription | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [audioPathModalOpen, setAudioPathModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isUploadingWithAudio, setIsUploadingWithAudio] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  // Redirect if no projectId available. Never while ownership is still being resolved — that is a
  // transient "no project", not a real one, and redirecting on it bounces the page on every load.
  useEffect(() => {
    if (!projectId && !isResolvingProject) {
      navigate('/projects', {replace: true});
    }
  }, [projectId, isResolvingProject, navigate]);

  const handleSelectTranscription = (transcription: Transcription) => {
    // Only completed rows have a CSV behind their r2_key; converting anything else would hand
    // /convertxml a key pointing at nothing.
    if (transcription.status !== 'completed') {
      return;
    }

    dispatch(setSelectedTranscription(transcription))
    if (projectId) {
      navigate(`/projects/${projectId}/workspace`);
    } else {
      navigate('/workspace');
    }
  };

  /**
   * A failed job leaves its audio untouched, so retrying is just another transcribe call against
   * the same upload — no re-upload, no new file to pick.
   */
  const handleRetry = async (transcription: Transcription) => {
    if (!transcription.audioUploadId) {
      return;
    }

    setRetryingId(transcription.id);

    const result = await dispatch(startTranscription({
      audioId: transcription.audioUploadId,
      outputFileName: transcription.name,
      ...(transcription.projectId && transcription.projectId !== 'all'
        ? {projectId: transcription.projectId}
        : {}),
    }));

    setRetryingId(null);

    if (startTranscription.fulfilled.match(result)) {
      showToast(`Retrying — saving as ${result.payload.name}.csv`, 'success');
      return;
    }

    // Already running somewhere the client lost track of (a reload, a second tab) rather than a
    // real failure — adopt the job the server names instead of reporting an error for it.
    if (result.payload?.status === 409 && result.payload.reason === 'duplicate_in_flight') {
      if (result.payload.transcriptionId) {
        dispatch(pollTranscriptionStatus(result.payload.transcriptionId));
      }
      showToast(`${transcription.name} is already transcribing — resuming.`, 'info');
      return;
    }

    showToast(result.payload?.message || 'Could not retry the transcription', 'error');
  };

  const error = reduxError;

  useEffect(() => {
    // Hold off while ownership of the requested project is still unknown — that is the window in
    // which an id from another account would go out unchecked.
    if (isResolvingProject) {
      return;
    }
    dispatch(invalidateCache());
    // If projectId is null we are in the default project — fetch every transcription
    loadTranscriptions(projectId || 'all');
  }, [projectId, isResolvingProject]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTranscriptions = async (projectFilter: string) => {
    dispatch(fetchTranscriptions(projectFilter));
  };

  const handleDeleteClick = (transcription: Transcription, e: React.MouseEvent) => {
    e.stopPropagation();
    setTranscriptionToDelete(transcription);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!transcriptionToDelete) return;

    setIsDeleting(true);
    try {
            // Delete from Worker API using Redux thunk
      await dispatch(deleteTranscription({key: transcriptionToDelete.csvR2Key}));

      setDeleteModalOpen(false);
      setTranscriptionToDelete(null);

            // Refresh the list
      const effectiveProjectId = projectId || defaultProjectId || undefined;
      if (effectiveProjectId) {
        await dispatch(fetchTranscriptions(effectiveProjectId));
      }
    } catch (err) {
      console.error('Failed to delete transcription:', err);
      alert(`Failed to delete transcription: ${err}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setTranscriptionToDelete(null);
  };

  const handleReuploadClick = async () => {
    const file = await pickFile({accept: '.csv,text/csv'});

    if (!file) {
      return; // User cancelled
    }

    setCsvFile(file);
    setAudioPathModalOpen(true);
  };

  const handleAudioPathConfirm = async (audioName: string) => {
    try {
      if (!csvFile) {
        throw new Error('No CSV file selected');
      }

      setAudioPathModalOpen(false);
      setIsUploadingWithAudio(true);

      const effectiveProjectId = projectId || defaultProjectId || undefined;

      const result = await dispatch(uploadCsvToWorker({
        csvFile,
        projectId: effectiveProjectId,
        audioName,
      })).unwrap();

      // Convert to local format and add to Redux
      const now = new Date().toISOString();
      const transcription: Transcription = {
        id: result.csv_key,
        name: csvFile.name.replace(/\.csv$/i, ''),
        csvR2Key: result.csv_key,
        projectId: effectiveProjectId || 'all',
        createdAt: now,
        audioPath: audioName,
        status: 'completed',
        updatedAt: now,
      };

      dispatch(addTranscription(transcription));
      showToast('Transcription uploaded successfully', 'success');
    } catch (err) {
      console.error('Failed to upload CSV:', err);
      showToast(`Failed to upload: ${err}`, 'error');
    } finally {
      setIsUploadingWithAudio(false);
      setCsvFile(null);
    }
  };

  const handleAudioPathCancel = () => {
    setAudioPathModalOpen(false);
    setCsvFile(null);
  };

  const filteredTranscriptions = transcriptions.filter((transcription) => {
    const name = transcription.name.toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  if (isLoading) {
    return (
      <div className="h-full bg-gradient-to-br from-gray-900 via-indigo-900 to-gray-900 flex items-center justify-center">
        <div className="flex items-center gap-3 text-blue-400">
          <div className="animate-spin h-6 w-6 border-2 border-blue-400 border-t-transparent rounded-full"></div>
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full bg-gradient-to-br from-gray-900 via-indigo-900 to-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-xl p-6 border border-gray-700">
          <div className="p-3 bg-red-900/30 border border-red-600 rounded mb-4">
            <p className="text-red-300 text-sm">Failed to load: {error}</p>
          </div>
          <button
            onClick={() => loadTranscriptions(projectId || 'all')}
            className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-500 text-sm font-medium cursor-pointer"
          >
                        🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-gray-900 via-indigo-900 to-gray-900 overflow-auto">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-gray-800 rounded-lg shadow-2xl p-5 border border-gray-700">
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-white mb-1">
                            📝 {project ? `${project.name} - Transcriptions` : 'Your Transcriptions'}
            </h1>
            <p className="text-gray-400 text-sm">
                            Select a transcription to convert to Final Cut Pro XML
            </p>
          </div>

          <div className="flex gap-2 mb-4">
            <button
              onClick={onNewTranscription}
              className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded hover:from-blue-500 hover:to-indigo-500 transition-all shadow text-sm font-medium cursor-pointer"
            >
                            ➕ Create New
            </button>
            <button
              onClick={handleReuploadClick}
              disabled={isUploadingWithAudio}
              className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-500 transition-colors text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              title="Import a CSV transcription made elsewhere"
            >
              {isUploadingWithAudio ? '⏳' : '📥 Import CSV'}
            </button>
            <button
              onClick={() => loadTranscriptions(projectId || 'all')}
              className="px-3 py-2 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors text-sm cursor-pointer"
            >
                            🔄 Refresh
            </button>
          </div>

          {transcriptions.length > 0 && (
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="🔍 Search..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 text-white rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 placeholder-gray-400 text-sm"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors text-sm cursor-pointer"
                  >
                                        ✕
                  </button>
                )}
              </div>
              {searchQuery && (
                <p className="text-xs text-gray-500 mt-1">
                                    Found {filteredTranscriptions.length} of {transcriptions.length}
                </p>
              )}
            </div>
          )}

          {transcriptions.length === 0 ? (
            <div className="text-center py-12 bg-gray-800/50 rounded border border-dashed border-gray-600">
              <div className="text-4xl mb-3">📄</div>
              <p className="text-gray-400 text-sm mb-4">No transcriptions found</p>
              <button
                onClick={onNewTranscription}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-500 transition-colors text-sm font-medium cursor-pointer"
              >
                                🎤 Create Your First Transcription
              </button>
            </div>
          ) : filteredTranscriptions.length === 0 ? (
            <div className="text-center py-12 bg-gray-800/50 rounded border border-dashed border-gray-600">
              <div className="text-4xl mb-3">🔍</div>
              <p className="text-gray-400 text-sm mb-2">No transcriptions match your search</p>
              <p className="text-gray-500 text-xs mb-4">Try a different search term</p>
              <button
                onClick={() => setSearchQuery('')}
                className="px-3 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors text-sm cursor-pointer"
              >
                                Clear Search
              </button>
            </div>
          ) : (
            <div className="bg-gray-900/50 rounded border border-gray-700 overflow-hidden">
              <div className="max-h-[500px] overflow-y-auto">
                {filteredTranscriptions.map((transcription) => {
                  const fileName = transcription.name;
                  const fileDate = transcription.createdAt ? new Date(transcription.createdAt).toLocaleDateString() : '';
                  const isProcessing = transcription.status === 'processing';
                  const isFailed = transcription.status === 'failed';
                  // Terminal like 'failed' — no CSV was ever produced — but the seconds are not
                  // refunded, so it gets its own label rather than being folded into "Failed".
                  const isRejected = transcription.status === 'rejected';
                  const isCompleted = transcription.status === 'completed';

                  return (
                    <div
                      key={transcription.id}
                      className={`w-full flex items-stretch border-b border-gray-700 transition-all group ${
                        isCompleted ? 'hover:bg-indigo-600/20' : ''
                      }`}
                    >
                      <button
                        onClick={() => handleSelectTranscription(transcription)}
                        disabled={!isCompleted}
                        className="flex-1 text-left px-3 py-2 cursor-pointer disabled:cursor-default"
                        title={
                          isCompleted
                            ? 'Convert this transcription'
                            : isRejected
                              ? 'Rejected — there is no CSV for this one'
                              : 'Available once the transcription finishes'
                        }
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className="text-lg">
                              {isProcessing ? (
                                <div className="animate-spin h-4 w-4 border-2 border-blue-400 border-t-transparent rounded-full"></div>
                              ) : (isFailed || isRejected) ? '⚠️' : '📄'}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium text-sm truncate transition-colors ${
                                isCompleted ? 'text-white group-hover:text-blue-300' : 'text-gray-300'
                              }`}>
                                {fileName}
                              </p>
                              <p className="text-xs text-gray-500 flex flex-wrap items-center gap-x-3">
                                {fileDate && <span>📅 {fileDate}</span>}
                                {isProcessing && <span className="text-blue-400">Transcribing…</span>}
                                {isFailed && <span className="text-red-400">Failed</span>}
                                {isRejected && <span className="text-red-400">Rejected</span>}
                              </p>
                              {(isFailed || isRejected) && transcription.error && (
                                <p className="text-xs text-red-400/80 mt-1 break-words">{transcription.error}</p>
                              )}
                            </div>
                          </div>
                          {isCompleted && (
                            <div className="ml-2 text-gray-500 group-hover:text-blue-400 transition-colors">
                              <span className="text-lg">→</span>
                            </div>
                          )}
                        </div>
                      </button>
                      {(isFailed || isRejected) && transcription.audioUploadId && (
                        <button
                          onClick={() => handleRetry(transcription)}
                          disabled={retryingId === transcription.id}
                          className="px-3 py-2 text-gray-400 hover:text-blue-300 hover:bg-blue-900/20 transition-colors
                           cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          title={
                            isRejected
                              ? 'Retry — the audio is still uploaded, so nothing is sent again. '
                                + 'The seconds from this attempt were not refunded.'
                              : 'Retry — the audio is still uploaded, so nothing is sent again'
                          }
                        >
                          {retryingId === transcription.id ? '⏳' : '🔄'}
                        </button>
                      )}
                      <button
                        onClick={e => handleDeleteClick(transcription, e)}
                        className="px-3 py-2 text-gray-500 hover:text-red-400 hover:bg-red-900/20 transition-colors cursor-pointer"
                        title="Delete transcription"
                      >
                                                🗑️
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {transcriptions.length > 0 && filteredTranscriptions.length > 0 && (
            <div className="mt-3 text-center text-gray-500 text-xs">
              {searchQuery ? `Showing ${filteredTranscriptions.length} of ${transcriptions.length}` : `${transcriptions.length} total`}
            </div>
          )}
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalOpen}
        listName={transcriptionToDelete?.name || null}
        isDeleting={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Delete Transcription?"
        message="Are you sure you want to delete"
      />

      <AudioPathModal
        isOpen={audioPathModalOpen}
        onConfirm={handleAudioPathConfirm}
        onCancel={handleAudioPathCancel}
        isLoading={isUploadingWithAudio}
      />
    </div>
  );
};

export default TranscriptionsPage;

